import {
  get,
  limitToFirst,
  onValue,
  orderByChild,
  query,
  ref,
  startAfter,
} from "firebase/database";
import { db } from "../lib/firebase";
import { ENTERPRISE_ADLE_MODULE_VALUES } from "../utils/constants";

const ENTERPRISE_INTAKE_PATH = "enterpriseIntake";

/** Same page size as pre-activation for consistent owner list UX. */
export const ENTERPRISE_INTAKE_PAGE_SIZE = 10;
const PAGE_FETCH = ENTERPRISE_INTAKE_PAGE_SIZE + 1;

const str = (v) => (v == null ? "" : String(v));
const arr = (v) => (Array.isArray(v) ? v : []);

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(str(value).trim());
};

const inferSelectedAdleModules = (raw = {}) => {
  if (
    Array.isArray(raw.selectedAdleModules) &&
    raw.selectedAdleModules.length
  ) {
    return raw.selectedAdleModules;
  }

  const modules = [];

  const hasModule1 =
    raw.module1BrandsOrVisualIdentitiesCount ||
    raw.module1MonthlyNewImagesCount ||
    raw.monthlyAiImages;

  const hasModule2 =
    raw.module2AdAccountsOrAdvertisersCount ||
    raw.module2MonthlyCampaignsCount ||
    raw.monthlyAiCampaigns ||
    hasValue(raw.phase1PaidChannels) ||
    raw.monthlyAdSpendRange ||
    raw.hasAdminAccessPhase1;

  const hasModule3 =
    raw.module3CampaignsToImportCount || hasValue(raw.module3ImportPlatforms);

  const hasModule4 =
    raw.module4MonthlyOrganicPostsCount ||
    hasValue(raw.module4SchedulingNetworks) ||
    raw.module4PostCreationSource ||
    raw.monthlyOrganicPosts ||
    raw.usesOrganicScheduling === "yes";

  if (hasModule1) modules.push(ENTERPRISE_ADLE_MODULE_VALUES.CREATIVE_ASSETS);
  if (hasModule2) modules.push(ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_LAUNCH);
  if (hasModule3) modules.push(ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_IMPORT);
  if (hasModule4) modules.push(ENTERPRISE_ADLE_MODULE_VALUES.ORGANIC_SOCIAL);

  return modules;
};

const normalizePlatformUsersCount = (raw = {}) => {
  const value = str(raw.platformUsersCount);
  if (value) return value;
  if (raw.platformUsersCountOther) return str(raw.platformUsersCountOther);
  return "";
};

/**
 * Normalizes RTDB payload to react-hook-form defaultValues for EnterpriseIntake.
 */
export function normalizeEnterpriseIntakeRecord(raw = {}) {
  return {
    fullName: str(raw.fullName),
    corporateEmail: str(raw.corporateEmail),
    companyName: str(raw.companyName),
    jobTitle: str(raw.jobTitle),
    companyWebsite: str(raw.companyWebsite),

    purchaseDecisionMaker: str(raw.purchaseDecisionMaker),
    currentToolOrAdleStatus: str(raw.currentToolOrAdleStatus),
    currentToolOrAdleStatusOther: str(raw.currentToolOrAdleStatusOther),
    operatingCountriesCount: str(raw.operatingCountriesCount),
    creativesVaryByMarket: str(raw.creativesVaryByMarket),

    selectedAdleModules: inferSelectedAdleModules(raw),
    primaryGoal: str(raw.primaryGoal),
    primaryGoalOther: str(raw.primaryGoalOther),

    module1BrandsOrVisualIdentitiesCount: str(
      raw.module1BrandsOrVisualIdentitiesCount
    ),
    module1IndependentBrandConfigsCount: str(
      raw.module1IndependentBrandConfigsCount
    ),
    module1MonthlyNewImagesCount: str(
      raw.module1MonthlyNewImagesCount ?? raw.monthlyAiImages
    ),
    module1FormatsPerPieceAvg: str(raw.module1FormatsPerPieceAvg),
    module1MonthlyAdditionalEditsCount: str(
      raw.module1MonthlyAdditionalEditsCount
    ),
    module1MonthlyVideosCount: str(
      raw.module1MonthlyVideosCount ?? raw.monthlyAiVideos
    ),
    module1MonthlyImageAnimationsCount: str(
      raw.module1MonthlyImageAnimationsCount
    ),
    module1VersionsPerPieceBeforeApproval: str(
      raw.module1VersionsPerPieceBeforeApproval
    ),
    module1CreativeVolumeVariation: str(raw.module1CreativeVolumeVariation),
    module1PeakMonthPiecesCount: str(raw.module1PeakMonthPiecesCount),

    module2AdAccountsOrAdvertisersCount: str(
      raw.module2AdAccountsOrAdvertisersCount
    ),
    module2MonthlyCampaignsCount: str(
      raw.module2MonthlyCampaignsCount ?? raw.monthlyAiCampaigns
    ),
    module2MonthlyOptimizationsCount: str(
      raw.module2MonthlyOptimizationsCount ?? raw.monthlyAiOptimizations
    ),
    module2CampaignVolumeVariation: str(raw.module2CampaignVolumeVariation),
    module2PeakMonthCampaignsCount: str(raw.module2PeakMonthCampaignsCount),

    module3CampaignsToImportCount: str(raw.module3CampaignsToImportCount),
    module3ImportPlatforms: arr(raw.module3ImportPlatforms),
    module3ImportPlatformsOther: str(raw.module3ImportPlatformsOther),

    module4MonthlyOrganicPostsCount: str(
      raw.module4MonthlyOrganicPostsCount ?? raw.monthlyOrganicPosts
    ),
    module4SchedulingNetworks: arr(raw.module4SchedulingNetworks),
    module4SchedulingNetworksOther: str(raw.module4SchedulingNetworksOther),
    module4PostCreationSource: str(raw.module4PostCreationSource),

    activeAdvertisersMonth1: str(raw.activeAdvertisersMonth1),
    activeAdvertisersMonth1Other: str(raw.activeAdvertisersMonth1Other),
    totalAdvertisers60to90Days: str(raw.totalAdvertisers60to90Days),
    totalAdvertisers60to90DaysOther: str(raw.totalAdvertisers60to90DaysOther),

    platformUsersCount: normalizePlatformUsersCount(raw),
    creditsManagementModel: str(raw.creditsManagementModel),
    creditsManagementModelOther: str(raw.creditsManagementModelOther),

    monthlyEstimatedCredits: str(raw.monthlyEstimatedCredits),
    monthlyEstimatedCreditsOther: str(raw.monthlyEstimatedCreditsOther),

    phase1PaidChannels: arr(raw.phase1PaidChannels),
    phase1PaidChannelsOther: str(raw.phase1PaidChannelsOther),
    monthlyAdSpendRange: str(raw.monthlyAdSpendRange),
    monthlyAdSpendRangeOther: str(raw.monthlyAdSpendRangeOther),
    hasAdminAccessPhase1: str(raw.hasAdminAccessPhase1),
    hasAdminAccessPhase1Other: str(raw.hasAdminAccessPhase1Other),

    supportLevel: str(raw.supportLevel),
    supportLevelOther: str(raw.supportLevelOther),
    platformManagementModel: str(raw.platformManagementModel),
    csmCadence: str(raw.csmCadence),
    csmCadenceOther: str(raw.csmCadenceOther),
    contractFormat: str(raw.contractFormat),
    contractFormatOther: str(raw.contractFormatOther),
    implementationTimeline: str(raw.implementationTimeline),
    implementationTimelineOther: str(raw.implementationTimelineOther),
    whiteLabelRequirement: str(raw.whiteLabelRequirement),
    urgencyDeadlineOrEvent: str(raw.urgencyDeadlineOrEvent),
    estimatedToolBudgetRange: str(raw.estimatedToolBudgetRange),
    securityRequirements: arr(raw.securityRequirements),
    securityRequirementsOther: str(raw.securityRequirementsOther),

    additionalNotes: str(raw.additionalNotes),
  };
}

export function enterpriseIntakeSnapshotToRows(snapshotVal) {
  if (!snapshotVal || typeof snapshotVal !== "object") return [];
  return Object.entries(snapshotVal)
    .map(([formId, data]) => {
      const n = normalizeEnterpriseIntakeRecord(data);
      return {
        formId,
        fullName: n.fullName,
        corporateEmail: n.corporateEmail,
        companyName: n.companyName,
        jobTitle: n.jobTitle,
      };
    })
    .sort((a, b) => {
      const A = `${a.companyName}${a.corporateEmail}`.toLowerCase();
      const B = `${b.companyName}${b.corporateEmail}`.toLowerCase();
      return A.localeCompare(B);
    });
}

/**
 * @param {(rows: ReturnType<typeof enterpriseIntakeSnapshotToRows>) => void} onData
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function listenEnterpriseIntakeSubmissions(onData, onError) {
  const dbRef = ref(db, ENTERPRISE_INTAKE_PATH);
  return onValue(
    dbRef,
    (snap) => {
      try {
        onData(enterpriseIntakeSnapshotToRows(snap.val()));
      } catch (e) {
        onError?.(e);
      }
    },
    (err) => onError?.(err)
  );
}

export async function fetchEnterpriseIntakeByFormId(formId) {
  if (!formId) return null;
  const dbRef = ref(db, `${ENTERPRISE_INTAKE_PATH}/${formId}`);
  const snap = await get(dbRef);
  if (!snap.exists()) return null;
  return normalizeEnterpriseIntakeRecord(snap.val());
}

export async function fetchEnterpriseIntakePage(cursorKey = null) {
  const baseRef = ref(db, ENTERPRISE_INTAKE_PATH);
  const hasCursor =
    cursorKey?.value != null && typeof cursorKey?.id === "string";
  const pageQuery = hasCursor
    ? query(
        baseRef,
        orderByChild("creationTimeNegative"),
        startAfter(cursorKey.value, cursorKey.id),
        limitToFirst(PAGE_FETCH)
      )
    : query(
        baseRef,
        orderByChild("creationTimeNegative"),
        limitToFirst(PAGE_FETCH)
      );

  const snap = await get(pageQuery);
  if (!snap.exists()) {
    return { rows: [], cursorAfter: null, hasMore: false };
  }

  const entries = [];
  snap.forEach((child) => {
    entries.push([child.key, child.val()]);
  });

  const hasMore = entries.length > ENTERPRISE_INTAKE_PAGE_SIZE;
  const slice = hasMore
    ? entries.slice(0, ENTERPRISE_INTAKE_PAGE_SIZE)
    : entries;

  const rows = slice.map(([formId, data]) => {
    const n = normalizeEnterpriseIntakeRecord(data);
    const creationTimeNegativeRaw = Number(data?.creationTimeNegative);
    const creationTimeNegative = Number.isNaN(creationTimeNegativeRaw)
      ? 0
      : creationTimeNegativeRaw;
    return {
      formId,
      companyName: n.companyName,
      fullName: n.fullName,
      corporateEmail: n.corporateEmail,
      jobTitle: n.jobTitle,
      creationTime:
        data?.creationTime ??
        (creationTimeNegative ? -creationTimeNegative : null),
      creationTimeNegative,
    };
  });
  const cursorAfter =
    rows.length > 0
      ? {
          value: rows[rows.length - 1].creationTimeNegative,
          id: rows[rows.length - 1].formId,
        }
      : null;

  return { rows, cursorAfter, hasMore };
}
